import React, { useState, useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import AuthContext from "../../context/AuthContext";
import {
  DollarSign, Plus, Edit, Trash2, Users,
  ChevronDown, ChevronUp, Activity, History, Receipt,
  X, Zap
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import budgetsService from "../../services/budgetsService";
import teamsService from "../../services/teamsService";
import organizationsService from "../../services/organizationsService";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";
import { useTranslation, useFormat } from "../../i18n";

const BudgetsSection = () => {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { user } = useContext(AuthContext);
  const { t } = useTranslation("admin_budgets");
  const fmt = useFormat();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [selectedBudgetForAllocation, setSelectedBudgetForAllocation] = useState(null);
  const [expandedBudgetId, setExpandedBudgetId] = useState(null);
  const [showMetrics, setShowMetrics] = useState(true);

  // Fetch budgets
  const { data: budgets = [], isLoading: budgetsLoading } = useQuery({
    queryKey: ["budgets"],
    queryFn: () => budgetsService.list(),
  });

  // Fetch organizations
  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => organizationsService.list(),
  });

  // Fetch teams
  const { data: teams = [] } = useQuery({
    queryKey: ["teams"],
    queryFn: () => teamsService.list(),
  });

  // The teams list includes soft-deleted (inactive) teams for admins; exclude
  // them from budget selection so you can't assign a budget to a deleted team.
  const activeTeams = teams.filter((t) => t.is_active !== false);

  // Create budget mutation
  const createMutation = useMutation({
    mutationFn: (data) => budgetsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgets"] });
      toast.success(t("budgets.toasts.created"));
      setShowCreateModal(false);
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Update budget mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => budgetsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgets"] });
      toast.success(t("budgets.toasts.updated"));
      setShowCreateModal(false);
      setEditingBudget(null);
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Delete budget mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => budgetsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgets"] });
      toast.success(t("budgets.toasts.deleted"));
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Allocate to team mutation
  const allocateMutation = useMutation({
    mutationFn: ({ budgetId, data }) => budgetsService.allocateToTeam(budgetId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgets"] });
      toast.success(t("budgets.toasts.allocated"));
      setShowAllocationModal(false);
      setSelectedBudgetForAllocation(null);
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Form for creating/editing budgets
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: "",
      amount: 10000,
      budget_type: "team",
      organization_id: "",
      team_id: "",
      admin_user_id: "",
      team_admin_id: "",
    },
  });

  // Form for allocating budgets
  const {
    register: registerAlloc,
    handleSubmit: handleAllocSubmit,
    reset: resetAlloc,
    formState: { errors: allocErrors }
  } = useForm({
    defaultValues: {
      team_id: "",
      amount: 1000,
      custom_name: "",
      team_admin_id: "",
    },
  });

  const onSubmit = (data) => {
    // Get organization_id from the selected team
    const selectedTeam = teams.find(t => t.id === data.team_id);
    const organizationId = selectedTeam?.organization_id || user?.organization_id || organizations[0]?.id;

    const payload = {
      name: data.name || t("budgets.default_name", { team: selectedTeam?.name || t("budgets.default_team") }),
      amount: parseFloat(data.amount),
      budget_type: "team",
      organization_id: organizationId,
      team_id: data.team_id,
      admin_user_id: user?.id,
      team_admin_user_id: user?.id,
    };

    if (editingBudget) {
      // Only the name and amount are editable; never send the create payload,
      // which would retype an organization budget as a team budget.
      updateMutation.mutate({
        id: editingBudget.id,
        data: { name: data.name, amount: parseFloat(data.amount) },
      });
    } else {
      createMutation.mutate(payload);
    }
  };

  const onAllocSubmit = (data) => {
    if (!selectedBudgetForAllocation) return;

    const payload = {
      team_id: data.team_id,
      amount: parseFloat(data.amount),
      custom_name: data.custom_name || undefined,
      team_admin_id: data.team_admin_id || undefined,
    };

    allocateMutation.mutate({
      budgetId: selectedBudgetForAllocation.id,
      data: payload
    });
  };

  const handleDelete = (id) => {
    if (window.confirm(t("budgets.delete_confirm"))) {
      deleteMutation.mutate(id);
    }
  };

  const openCreateModal = () => {
    setEditingBudget(null);
    reset({
      name: "",
      amount: 10000,
      budget_type: "team",
      organization_id: "",
      team_id: activeTeams[0]?.id || "",
      admin_user_id: "",
      team_admin_id: "",
    });
    setShowCreateModal(true);
  };

  const openEditModal = (budget) => {
    setEditingBudget(budget);
    reset({
      name: budget.name,
      amount: budget.amount,
      budget_type: "team",
      organization_id: budget.organization_id || "",
      team_id: budget.team_id || "",
      admin_user_id: budget.admin_user_id || "",
      team_admin_id: budget.team_admin_id || "",
    });
    setShowCreateModal(true);
  };

  const openAllocationModal = (budget) => {
    setSelectedBudgetForAllocation(budget);
    resetAlloc({
      team_id: activeTeams[0]?.id || "",
      amount: 1000,
      custom_name: "",
      team_admin_id: "",
    });
    setShowAllocationModal(true);
  };

  // Calculate metrics - ensure values are parsed as numbers
  const totalBudget = budgets.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
  const totalUsed = budgets.reduce((sum, b) => sum + (parseFloat(b.used_amount) || 0), 0);
  const totalReserved = budgets.reduce((sum, b) => sum + (parseFloat(b.reserved_amount) || 0), 0);
  const totalRemaining = totalBudget - totalUsed - totalReserved;
  const usagePercentage = totalBudget > 0 ? ((totalUsed + totalReserved) / totalBudget) * 100 : 0;

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center gap-2">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-slate-800 truncate">{t("budgets.title")}</h1>
          <p className="text-xs sm:text-sm text-slate-600 mt-0.5 sm:mt-1 truncate">{t("budgets.subtitle")}</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 org-gradient org-btn text-white rounded-full transition-all shadow-md text-sm flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">{t("budgets.create_budget")}</span>
          <span className="sm:hidden">{t("budgets.new_short")}</span>
        </button>
      </div>

      {/* Budget Metrics Card */}
      <AnimatePresence>
        {showMetrics && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-2xl shadow-md overflow-hidden"
          >
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-600" />
                <h2 className="font-semibold text-slate-800">{t("budgets.overview")}</h2>
              </div>
              <button
                onClick={() => setShowMetrics(!showMetrics)}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
              >
                <ChevronUp className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl border border-purple-200">
                  <p className="text-xs text-purple-700 font-medium mb-1">{t("budgets.metrics.total_budget")}</p>
                  <p className="text-2xl font-bold text-purple-600">{fmt.currency(totalBudget, "USD")}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border border-green-200">
                  <p className="text-xs text-green-700 font-medium mb-1">{t("budgets.metrics.used")}</p>
                  <p className="text-2xl font-bold text-green-600">{fmt.currency(totalUsed, "USD")}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl border border-yellow-200">
                  <p className="text-xs text-yellow-700 font-medium mb-1">{t("budgets.metrics.reserved")}</p>
                  <p className="text-2xl font-bold text-yellow-600">{fmt.currency(totalReserved, "USD")}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border border-blue-200">
                  <p className="text-xs text-blue-700 font-medium mb-1">{t("budgets.metrics.remaining")}</p>
                  <p className="text-2xl font-bold text-blue-600">{fmt.currency(totalRemaining, "USD")}</p>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-700">{t("budgets.overall_usage")}</span>
                  <span className="text-sm font-semibold text-slate-900">{fmt.number(usagePercentage / 100, { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
                </div>
                <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${getProgressColor(usagePercentage)}`}
                    style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!showMetrics && (
        <button
          onClick={() => setShowMetrics(true)}
          className="w-full p-3 bg-white rounded-full shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 text-slate-600"
        >
          <ChevronDown className="w-5 h-5" />
          <span className="text-sm font-medium">{t("budgets.show_metrics")}</span>
        </button>
      )}

      {/* Budgets List */}
      {budgetsLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
        </div>
      ) : budgets.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-md p-12 text-center">
          <DollarSign className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">{t("budgets.empty.title")}</h3>
          <p className="text-slate-500 mb-6">{t("budgets.empty.subtitle")}</p>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 org-gradient org-btn text-white rounded-full transition-all shadow-md inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t("budgets.create_budget")}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {budgets.map((budget) => (
            <BudgetCard
              key={budget.id}
              budget={budget}
              expanded={expandedBudgetId === budget.id}
              onToggleExpand={() => setExpandedBudgetId(expandedBudgetId === budget.id ? null : budget.id)}
              onEdit={() => openEditModal(budget)}
              onDelete={() => handleDelete(budget.id)}
              onAllocate={() => openAllocationModal(budget)}
              isDeleting={deleteMutation.isPending}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Budget Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => {
                setShowCreateModal(false);
                setEditingBudget(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-200 sticky top-0 bg-white flex justify-between items-center">
                <h2 className="text-xl font-semibold text-slate-900">
                  {editingBudget ? t("budgets.modal.edit_title") : t("budgets.modal.create_title")}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingBudget(null);
                  }}
                  className="p-1 hover:bg-slate-100 rounded transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("budgets.form.name_label")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("name", { required: t("budgets.form.name_required") })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={t("budgets.form.name_placeholder")}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("budgets.form.amount_label")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register("amount", {
                      required: t("budgets.form.amount_required"),
                      min: { value: 1, message: t("budgets.form.amount_min") }
                    })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="10000"
                  />
                  {errors.amount && (
                    <p className="text-xs text-red-500 mt-1">{errors.amount.message}</p>
                  )}
                </div>

                {!editingBudget && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("budgets.form.team_label")} <span className="text-red-500">*</span>
                    </label>
                    <select
                      {...register("team_id", { required: !editingBudget ? t("budgets.form.team_required") : false })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">{t("budgets.form.select_team")}</option>
                      {activeTeams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                    {errors.team_id && (
                      <p className="text-xs text-red-500 mt-1">{errors.team_id.message}</p>
                    )}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingBudget(null);
                    }}
                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
                  >
                    {t("common:actions.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="flex-1 px-4 py-2 org-gradient org-btn text-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {(createMutation.isPending || updateMutation.isPending) ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {editingBudget ? t("budgets.form.updating") : t("budgets.form.creating")}
                      </>
                    ) : (
                      editingBudget ? t("budgets.form.update_budget") : t("budgets.form.create_budget")
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Allocation Modal */}
      <AnimatePresence>
        {showAllocationModal && selectedBudgetForAllocation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => {
                setShowAllocationModal(false);
                setSelectedBudgetForAllocation(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-xl w-full max-w-md"
            >
              <div className="p-6 border-b border-slate-200 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-slate-900">{t("budgets.allocation.title")}</h2>
                <button
                  onClick={() => {
                    setShowAllocationModal(false);
                    setSelectedBudgetForAllocation(null);
                  }}
                  className="p-1 hover:bg-slate-100 rounded transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              <form onSubmit={handleAllocSubmit(onAllocSubmit)} className="p-6 space-y-4">
                <div className="p-3 bg-blue-50 rounded-2xl border border-blue-200">
                  <p className="text-sm text-blue-800">
                    {t("budgets.allocation.allocating_from")} <span className="font-semibold">{selectedBudgetForAllocation.name}</span>
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    {t("budgets.allocation.available", { amount: fmt.currency(selectedBudgetForAllocation.amount - (selectedBudgetForAllocation.used_amount || 0) - (selectedBudgetForAllocation.reserved_amount || 0), "USD") })}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("budgets.form.team_label")} <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...registerAlloc("team_id", { required: t("budgets.form.team_required") })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">{t("budgets.form.select_team")}</option>
                    {activeTeams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                  {allocErrors.team_id && (
                    <p className="text-xs text-red-500 mt-1">{allocErrors.team_id.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("budgets.form.amount_label")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...registerAlloc("amount", {
                      required: t("budgets.form.amount_required"),
                      min: { value: 1, message: t("budgets.form.amount_min") }
                    })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="1000"
                  />
                  {allocErrors.amount && (
                    <p className="text-xs text-red-500 mt-1">{allocErrors.amount.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("budgets.allocation.custom_name_label")}
                  </label>
                  <input
                    type="text"
                    {...registerAlloc("custom_name")}
                    className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={t("budgets.allocation.custom_name_placeholder")}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAllocationModal(false);
                      setSelectedBudgetForAllocation(null);
                    }}
                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
                  >
                    {t("common:actions.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={allocateMutation.isPending}
                    className="flex-1 px-4 py-2 org-gradient org-btn text-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {allocateMutation.isPending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t("budgets.allocation.allocating")}
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4" />
                        {t("budgets.allocation.submit")}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Budget Card Component
const BudgetCard = ({ budget, expanded, onToggleExpand, onEdit, onDelete, onAllocate, isDeleting }) => {
  const { t } = useTranslation("admin_budgets");
  const fmt = useFormat();
  const budgetAmount = parseFloat(budget.amount) || 0;
  const usedAmount = parseFloat(budget.used_amount) || 0;
  const reservedAmount = parseFloat(budget.reserved_amount) || 0;
  const remaining = budgetAmount - usedAmount - reservedAmount;
  const usagePercentage = budgetAmount > 0 ? ((usedAmount + reservedAmount) / budgetAmount) * 100 : 0;

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden">
      <div className="p-3 sm:p-6">
        <div className="flex items-start justify-between gap-2 mb-3 sm:mb-4">
          <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-2xl org-gradient-br flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm sm:text-lg font-semibold text-slate-900 truncate">{budget.name}</h3>
              <div className="flex items-center gap-2 mt-0.5 sm:mt-1">
                <span className="px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-100 text-blue-700">
                  {t("budgets.card.type_team")}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-0.5 sm:gap-2 flex-shrink-0">
            <button
              onClick={onEdit}
              className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-full transition-colors"
              title={t("budgets.card.edit_title")}
            >
              <Edit className="w-4 h-4 text-slate-600" />
            </button>
            <button
              onClick={onDelete}
              disabled={isDeleting}
              className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-full transition-colors disabled:opacity-50"
              title={t("budgets.card.delete_title")}
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
            <button
              onClick={onToggleExpand}
              className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              {expanded ? (
                <ChevronUp className="w-4 h-4 text-slate-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-600" />
              )}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-4">
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500 mb-0.5 sm:mb-1">{t("budgets.metrics.total")}</p>
            <p className="text-sm sm:text-lg font-bold text-slate-900 truncate">{fmt.currency(budgetAmount, "USD")}</p>
          </div>
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500 mb-0.5 sm:mb-1">{t("budgets.metrics.used")}</p>
            <p className="text-sm sm:text-lg font-bold text-green-600 truncate">{fmt.currency(usedAmount, "USD")}</p>
          </div>
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500 mb-0.5 sm:mb-1">{t("budgets.metrics.reserved")}</p>
            <p className="text-sm sm:text-lg font-bold text-yellow-600 truncate">{fmt.currency(reservedAmount, "USD")}</p>
          </div>
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500 mb-0.5 sm:mb-1">{t("budgets.metrics.remaining")}</p>
            <p className="text-sm sm:text-lg font-bold text-blue-600 truncate">{fmt.currency(remaining, "USD")}</p>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1 sm:mb-2">
            <span className="text-[10px] sm:text-xs font-medium text-slate-700">{t("budgets.usage")}</span>
            <span className="text-[10px] sm:text-xs font-semibold text-slate-900">{fmt.number(usagePercentage / 100, { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${getProgressColor(usagePercentage)}`}
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-slate-200"
          >
            <div className="p-3 sm:p-6 bg-slate-50 space-y-2 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
              <div className="p-2.5 sm:p-4 bg-white rounded-2xl border border-slate-200 flex items-center gap-2 sm:block">
                <History className="w-4 h-4 text-slate-600 flex-shrink-0" />
                <div className="min-w-0 flex-1 sm:flex-none">
                  <h4 className="text-xs sm:text-sm font-semibold text-slate-800 sm:mb-2 truncate">{t("budgets.card.reservations")}</h4>
                  <p className="hidden sm:block text-xs text-slate-500">{t("budgets.card.coming_soon")}</p>
                </div>
                <span className="text-[10px] sm:hidden text-slate-500 flex-shrink-0">{t("budgets.card.coming_soon_short")}</span>
              </div>
              <div className="p-2.5 sm:p-4 bg-white rounded-2xl border border-slate-200 flex items-center gap-2 sm:block">
                <Receipt className="w-4 h-4 text-slate-600 flex-shrink-0" />
                <div className="min-w-0 flex-1 sm:flex-none">
                  <h4 className="text-xs sm:text-sm font-semibold text-slate-800 sm:mb-2 truncate">{t("budgets.card.transactions")}</h4>
                  <p className="hidden sm:block text-xs text-slate-500">{t("budgets.card.coming_soon")}</p>
                </div>
                <span className="text-[10px] sm:hidden text-slate-500 flex-shrink-0">{t("budgets.card.coming_soon_short")}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BudgetsSection;
